// /frontend/src/components/rfq/EnhancedTimeline.jsx
import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaCalendarAlt, FaUser, FaClipboardList, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { proposalService } from '../../services/proposalService';

const EnhancedTimeline = ({ cart, className = '' }) => {
  const [expandedMilestones, setExpandedMilestones] = useState({});
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline', 'gantt', 'phases'
  
  const timeline = proposalService.generateProjectTimeline();
  const phaseStats = proposalService.getPhaseStatistics(timeline);
  const riskAssessment = proposalService.generateRiskAssessment();

  const toggleMilestone = (milestoneId) => {
    setExpandedMilestones(prev => ({
      ...prev,
      [milestoneId]: !prev[milestoneId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPhaseColor = (phase) => {
    const phaseConfig = proposalService.projectPhases[phase];
    if (!phaseConfig) return 'bg-gray-100 text-gray-800';
    
    return `${phaseConfig.bgColor} ${phaseConfig.textColor}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return (
          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'pending':
        return (
          <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center">
            <FaClock className="w-4 h-4 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
          </div>
        );
    }
  };

  const TimelineView = () => (
    <div className="space-y-4">
      {Object.entries(timeline).map(([key, milestone], index) => (
        <div key={key} className="relative">
          <div className="flex items-start space-x-4">
            {/* Timeline connector */}
            <div className="flex flex-col items-center">
              {getStatusIcon(milestone.status)}
              {index < Object.keys(timeline).length - 1 && (
                <div className="w-px h-16 bg-gray-200 mt-2"></div>
              )}
            </div>

            {/* Milestone content */}
            <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-medium text-gray-900">{milestone.title}</h4>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(milestone.status)}`}>
                      {milestone.status.toUpperCase()}
                    </span>
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded" style={{ backgroundColor: proposalService.projectPhases[milestone.phase]?.bgColor, color: proposalService.projectPhases[milestone.phase]?.textColor }}>
                      {milestone.phase.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-3">{milestone.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center text-gray-500">
                      <FaCalendarAlt className="mr-2" />
                      <span>{milestone.date.toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <FaClock className="mr-2" />
                      <span>{milestone.duration}</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <FaUser className="mr-2" />
                      <span>{milestone.responsible}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => toggleMilestone(key)}
                  className="ml-4 p-1 text-gray-400 hover:text-gray-600"
                >
                  {expandedMilestones[key] ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>

              {/* Expanded details */}
              {expandedMilestones[key] && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="flex items-center font-medium text-gray-900 mb-2">
                        <FaClipboardList className="mr-2" />
                        Deliverables
                      </h5>
                      <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                        {milestone.deliverables?.map((deliverable, idx) => (
                          <li key={idx}>{deliverable}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Additional Details</h5>
                      <div className="text-sm text-gray-600 space-y-2">
                        <p><strong>Phase:</strong> {milestone.phase.charAt(0).toUpperCase() + milestone.phase.slice(1)}</p>
                        <p><strong>Responsible Team:</strong> {milestone.responsible}</p>
                        <p><strong>Estimated Duration:</strong> {milestone.duration}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const PhaseView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Object.entries(phaseStats).map(([phaseName, stats]) => {
        const phaseConfig = proposalService.projectPhases[phaseName];
        if (!phaseConfig) return null;

        return (
          <div 
            key={phaseName}
            className="border rounded-lg p-4"
            style={{ 
              borderColor: phaseConfig.borderColor,
              backgroundColor: phaseConfig.bgColor 
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 
                className="font-medium text-lg capitalize"
                style={{ color: phaseConfig.textColor }}
              >
                {phaseName}
              </h3>
              <span 
                className="px-2 py-1 rounded text-sm font-medium"
                style={{ 
                  backgroundColor: phaseConfig.color + '20',
                  color: phaseConfig.textColor 
                }}
              >
                {stats.count} milestone{stats.count !== 1 ? 's' : ''}
              </span>
            </div>
            
            <p className="text-sm mb-4" style={{ color: phaseConfig.textColor }}>
              {phaseConfig.description}
            </p>
            
            <div className="space-y-2">
              {stats.milestones.map((milestone, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span style={{ color: phaseConfig.textColor }}>{milestone.title}</span>
                  <span className="text-xs text-gray-500">{milestone.duration}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  const RiskAssessmentView = () => (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
      <h3 className="flex items-center text-lg font-medium text-yellow-800 mb-4">
        <FaExclamationTriangle className="mr-2" />
        Risk Assessment & Mitigation
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {riskAssessment.map((risk, index) => (
          <div key={index} className="bg-white border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-medium text-gray-900">{risk.category}</h4>
              <div className="flex space-x-2">
                <span className={`px-2 py-1 text-xs rounded ${
                  risk.probability === 'High' ? 'bg-red-100 text-red-700' :
                  risk.probability === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {risk.probability} Prob.
                </span>
                <span className={`px-2 py-1 text-xs rounded ${
                  risk.impact === 'High' ? 'bg-red-100 text-red-700' :
                  risk.impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {risk.impact} Impact
                </span>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-2">{risk.risk}</p>
            
            <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
              <strong>Mitigation:</strong> {risk.mitigation}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`${className}`}>
      {/* View mode selector */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">Project Timeline & Details</h3>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              viewMode === 'timeline' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Timeline
          </button>
          <button
            onClick={() => setViewMode('phases')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              viewMode === 'phases' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Phases
          </button>
          <button
            onClick={() => setViewMode('risks')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              viewMode === 'risks' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Risks
          </button>
        </div>
      </div>

      {/* Content based on view mode */}
      <div className="min-h-96">
        {viewMode === 'timeline' && <TimelineView />}
        {viewMode === 'phases' && <PhaseView />}
        {viewMode === 'risks' && <RiskAssessmentView />}
      </div>

      {/* Project summary */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4 pt-6 border-t border-gray-200">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {Object.keys(timeline).length}
          </div>
          <div className="text-sm text-gray-600">Total Milestones</div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            ~{proposalService.calculateProjectDuration(timeline)}
          </div>
          <div className="text-sm text-gray-600">Calendar Days</div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">
            {Object.keys(phaseStats).length}
          </div>
          <div className="text-sm text-gray-600">Project Phases</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">
            {cart.items.reduce((sum, item) => sum + item.quantity, 0)}
          </div>
          <div className="text-sm text-gray-600">Items to Deliver</div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedTimeline;